import {
  Message,
  useToaster,
  SelectPicker,
  Input,
  Radio,
  RadioGroup,
  TagPicker,
  Checkbox,
} from "rsuite";
import "./_upsert-app.scss";
import CloseIcon from "@rsuite/icons/Close";
import SaveIcon from "@rsuite/icons/Save";
import CopyIcon from "@rsuite/icons/Copy";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import { useEffect, useState, useCallback, useRef } from "react";
import React from "react";
import {
  AppMode,
  IApp,
  IDomain,
  IUpsertAppRequest,
} from "@bprotsyk/aso-core";
import { createOfferwallAppState } from './offerwall-app-state';
import {
  AppValidationErrors,
  AppValidationField,
  readServerValidationErrors,
  validateOfferwallAppForm,
} from './app-validation';
import { AppPlatform } from "constants/app-initial";
import { AppJSON } from "./json/app-json";
import { IUpsertStubRequest, PanelService } from "network/panel/network";
import type { ISectionsList } from "@bprotsyk/aso-core/lib/offers/list";
import { createLogger } from '../../../../utils/logger';

const logger = createLogger('upsert-app');

interface IProps {
  onClose: () => void;
  app?: IApp;
  create?: boolean;
  selectedIntegration?: string | null;
  preloadedSectionsLists?: ISectionsList[];
}

const AUTO_DOMAIN_VALUE = '__pdl_auto_domain__';
const AUTO_DOMAIN_OPTION = {
  label: 'Купити дешевий домен автоматично',
  value: AUTO_DOMAIN_VALUE,
};

const PASSWORD = "123456";

type TabKey = "general" | "json";

const placement = "topEnd";
function getSaveErrorText(error: any) {
  const responseData = error?.response?.data;
  if (typeof responseData === 'string' && responseData.trim()) {
    return responseData;
  }
  if (responseData?.message) {
    return String(responseData.message);
  }
  if (responseData?.error?.message) {
    return String(responseData.error.message);
  }
  if (error?.message) {
    return String(error.message);
  }
  return "Помилка при збереженні додатку";
}

export default function UpsertAppModal({
  onClose,
  app,
  create,
  selectedIntegration,
  preloadedSectionsLists,
}: IProps) {
  const toaster = useToaster();
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    if (selectedIntegration === "settings") {
      return "json";
    }
    return (selectedIntegration as TabKey) || "general";
  });
  const [appStub, setAppStub] = useState<IUpsertStubRequest | null>(null);
  const [appState, setAppState] = useState<IApp | IUpsertAppRequest>(() => createOfferwallAppState(app));
  const [useOfferwallPath, setUseOfferwallPath] = useState(() => {
    const params = app?.offerwallParams as Record<string, any> | undefined;
    const hasStoredPath = Boolean(params && Object.prototype.hasOwnProperty.call(params, 'path'));
    return !hasStoredPath || Boolean(String(params?.path || '').trim());
  });
  const [isGeneratingOfferwallPath, setIsGeneratingOfferwallPath] = useState(false);
  const offerwallPathRequestRef = useRef(0);
  const offerwallPathRequestInFlightRef = useRef(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const closeWithAnimation = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);
  logger.debug('activeTab', { activeTab, selectedIntegration });

  // Track if app has been modified
  const [hasChanges, setHasChanges] = useState(false);
  const [originalApp, setOriginalApp] = useState<IApp | IUpsertAppRequest | null>(null);
  
  // Validation errors
  const [validationErrors, setValidationErrors] = useState<AppValidationErrors>({});

  const generateOfferwallPath = useCallback(async () => {
    if (offerwallPathRequestInFlightRef.current) return;
    offerwallPathRequestInFlightRef.current = true;
    const requestId = ++offerwallPathRequestRef.current;
    setIsGeneratingOfferwallPath(true);
    setValidationErrors((current) => {
      const next = { ...current };
      delete next.offerwallPath;
      return next;
    });

    try {
      const path = await PanelService.generateOfferwallPath(Number((app as any)?.id || 0) || undefined);
      if (!/^[a-z]{5,6}$/.test(path)) {
        throw new Error('Invalid generated offerwall path');
      }
      if (requestId !== offerwallPathRequestRef.current) return;
      setAppState((current) => ({
        ...current,
        offerwallParams: {
          ...(current.offerwallParams || {}),
          path,
        },
      } as IUpsertAppRequest));
    } catch (error) {
      if (requestId !== offerwallPathRequestRef.current) return;
      logger.error('Error generating offerwall path', error);
      setValidationErrors((current) => ({
        ...current,
        offerwallPath: 'Не вдалося згенерувати шлях офервола. Зніміть і знову встановіть галочку.',
      }));
    } finally {
      if (requestId === offerwallPathRequestRef.current) {
        offerwallPathRequestInFlightRef.current = false;
        setIsGeneratingOfferwallPath(false);
      }
    }
  }, [app]);

  useEffect(() => {
    if (useOfferwallPath && !String(appState.offerwallParams?.path || '').trim()) {
      void generateOfferwallPath();
    }
  }, [appState.offerwallParams?.path, generateOfferwallPath, useOfferwallPath]);

  // Close modal on ESC if no changes
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !hasChanges) {
        closeWithAnimation();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [hasChanges, closeWithAnimation]);

  // Initialize original app state for change detection
  useEffect(() => {
    if (app) {
      setOriginalApp(JSON.parse(JSON.stringify(createOfferwallAppState(app))));
    } else if (create) {
      setOriginalApp(JSON.parse(JSON.stringify(createOfferwallAppState())));
    }
  }, [app, create]);

  const [campaignMode, setCampaignMode] = useState<'create' | 'select'>('create');
  const [appxCampaigns, setAppxCampaigns] = useState<Array<{ id: number; name: string; group_id?: number; state?: string }>>([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);
  const [usedCampaignMap, setUsedCampaignMap] = useState<Map<number, number>>(new Map());
  const [originalCampaignId, setOriginalCampaignId] = useState<number | null>(null);
  const [originalCampaignMode, setOriginalCampaignMode] = useState<'create' | 'select'>('create');

  useEffect(() => {
    if (originalApp && appState) {
      const hasAppChanged = JSON.stringify(originalApp) !== JSON.stringify(appState);
      const hasCampaignChanged = campaignMode !== originalCampaignMode || selectedCampaignId !== originalCampaignId;
      setHasChanges(hasAppChanged || hasCampaignChanged);
    }
  }, [appState, originalApp, campaignMode, selectedCampaignId, originalCampaignMode, originalCampaignId]);

  const [isEditingAllowed, setIsEditingAllowed] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState("");
  const [availableDomain, setAvailableDomain] = useState<IDomain[]>([]);
  const [selectedDomain, setSelectedDomain] = useState<string | null>(() => create && !app ? AUTO_DOMAIN_VALUE : null);
  // const [stubData, setStubData] = useState<IAppStubParams | null>(null); // offersStubParams removed
  
  // Sections lists for offerwall - use preloaded if available
  const [sectionsLists, setSectionsLists] = useState<ISectionsList[]>(preloadedSectionsLists || []);
  
  // Update sections lists when preloaded data becomes available
  useEffect(() => {
    if (preloadedSectionsLists && preloadedSectionsLists.length > 0) {
      setSectionsLists(preloadedSectionsLists);
    }
  }, [preloadedSectionsLists]);

  const handleSectionsListChange = (value: number | null) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            offerwallParams: {
              ...(prevState.offerwallParams || {}),
              sectionsListId: value,
            },
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };


  useEffect(() => {
    const filter = '/?target=main';
    PanelService.getDomains(filter)
      .then((res) => setAvailableDomain(Array.isArray(res) ? res : []))
      .catch((err) => {
        setAvailableDomain([]);
        logger.error('Error fetching domains', err);
      });
    
    // Fetch sections lists for offerwall dropdown (skip if preloaded)
    if (!preloadedSectionsLists || preloadedSectionsLists.length === 0) {
    PanelService.getSectionsLists().then((res) => {
      setSectionsLists(res);
    }).catch((err) => {
      logger.error('Error fetching sections lists', err);
    });
    }
    PanelService.getAppxCampaigns()
      .then((rows) => setAppxCampaigns(Array.isArray(rows) ? rows : []))
      .catch((err) => logger.error('Error fetching appx campaigns', err));
    PanelService.getUsedCampaignIds()
      .then((rows) => {
        const map = new Map<number, number>();
        rows.forEach((r) => map.set(r.campaignId, r.appId));
        setUsedCampaignMap(map);
      })
      .catch((err) => logger.error('Error fetching used campaign IDs', err));
  }, [preloadedSectionsLists]);

  useEffect(() => {
    if ((app as any)?.campaign?.campaignId) {
      setCampaignMode('select');
      setSelectedCampaignId((app as any)?.campaign?.campaignId);
      setOriginalCampaignId((app as any)?.campaign?.campaignId);
      setOriginalCampaignMode('select');
    }
  }, [app]);

  const domainOptions = [
    ...(create && !app ? [AUTO_DOMAIN_OPTION] : []),
    ...availableDomain.map((domain) => ({
      label: domain.name,
      value: String(domain.id),
    })),
  ];

  useEffect(() => {
    if (!create && appState.domainParams?.name && domainOptions?.length > 0 && !selectedDomain) {
      const match = domainOptions.find((o) => o.label === appState.domainParams?.name);
      if (match) setSelectedDomain(match.value);
    }
  }, [domainOptions, appState.domainParams?.name]);


  const focusFirstInvalidField = (errors: AppValidationErrors) => {
    const fieldOrder: AppValidationField[] = ['appId', 'appPlatform', 'domain', 'offerwallPath', 'bundle', 'name'];
    const firstField = fieldOrder.find((field) => Boolean(errors[field]));
    if (!firstField) return;
    setActiveTab('general');
    window.setTimeout(() => {
      const field = document.querySelector(`[data-validation-field="${firstField}"]`);
      const control = field?.querySelector<HTMLElement>('input, button, [role="combobox"]');
      control?.focus();
      field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  };

  const validateForm = () => {
    const errors = validateOfferwallAppForm(appState, {
      isCreating: Boolean(create && !app),
      useOfferwallPath,
    });
    setValidationErrors(errors);
    focusFirstInvalidField(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate before saving
    if (!validateForm()) {
      setSubmitError('Перевірте поля, позначені нижче.');
      return;
    }
    setSubmitError('');

    if (campaignMode === 'select' && selectedCampaignId) {
      const usedByAppId = usedCampaignMap.get(selectedCampaignId);
      if (usedByAppId !== undefined && usedByAppId !== appState.id) {
        toaster.push(
          message("error", `Кампанія #${selectedCampaignId} вже використовується додатком #${usedByAppId}`),
          { placement, duration: 5000 }
        );
        return;
      }

      if (selectedCampaignId !== originalCampaignId) {
        const confirmed = window.confirm(
          'Увага! Будуть оновлені керовані потоки кампанії (Geo Filter + офери з sub_id_30 + замикаючий). Кастомні потоки буде збережено. Продовжити?'
        );
        if (!confirmed) return;
      }
    }
    
    setIsSaving(true);
    const savePromise = new Promise(async (resolve, reject) => {
      try {
        const cleanedAppState = {
          ...appState,
          appMode: AppMode.OFFERWALL,
          createApp: Boolean(create && !app),
          enabled: true,
          offerwallParams: {
            ...(appState.offerwallParams || {}),
            defaultLogicEnabled: true,
          },
          createNewCampaign: campaignMode === 'create',
          selectedOfferwallCampaignId: campaignMode === 'select' ? selectedCampaignId : null,
          selectedCampaignId: campaignMode === 'select' ? selectedCampaignId : null,
        } as unknown as IUpsertAppRequest;
        delete (cleanedAppState as any).campaignNaming;
        const appResponse = await PanelService.upsertApp(cleanedAppState);
        setAppState(appResponse.data);
        
        if(appStub){
          const stubResponse = await PanelService.upsertStub(appStub);
          // setStubData(stubResponse.data); // stubData removed
        }
        resolve(appResponse.data);
      } catch (error) {
        reject(error);
      }
    });

    savePromise
      .then((data: any) => {
        toaster.push(
          message("success", "Додаток успішно збережено"),
          { placement, duration: 5000 }
        );
        closeWithAnimation();
      })
      .catch((error) => {
        logger.error('Error saving app', error);
        const serverErrors = readServerValidationErrors(error);
        if (Object.keys(serverErrors).length > 0) {
          setValidationErrors((current) => ({ ...current, ...serverErrors }));
          focusFirstInvalidField(serverErrors);
        }
        setSubmitError(getSaveErrorText(error));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handlePasswordSubmit = () => {
    if (password === PASSWORD) {
      setIsEditingAllowed(true);
      setShowPasswordModal(false);
      setPassword("");
    } else {
      toaster.push(
        message("error", "Невірний пароль"),
        { placement, duration: 3000 }
      );
    }
  };

  const handleEditAttempt = () => {
    if (!isEditingAllowed) {
      setShowPasswordModal(true);
    }
  };

  const message = (messageType: any, messageText: string) => {
    return (
      <Message key={new Date().getTime()} showIcon type={messageType} closable>
        <strong>{messageText}</strong>
      </Message>
    );
  };

  const clearValidationError = (field: AppValidationField) => {
    setValidationErrors((current) => {
      const next = { ...current };
      delete next[field];
      return next;
    });
    setSubmitError('');
  };

  const handleBundleChange = (value: string) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            bundle: value,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const handleAppIdChange = (value: string) => {
    const digitsOnly = String(value || '').replace(/\D/g, '');
    const nextId = digitsOnly ? Number(digitsOnly) : 0;
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            id: nextId,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
    if (validationErrors.appId) {
      clearValidationError('appId');
    }
  };

  const handleNameChange = (value: string) => {
    setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            name: value,
          } as IApp | IUpsertAppRequest)
        : prevState
    );
  };

  const updateApp = (updatedApp: IApp | IUpsertAppRequest) => {
    setAppState(updatedApp);
  };

  const handleDomainChange = (value: string | null) => {
    const nextValue = value || (create && !app ? AUTO_DOMAIN_VALUE : null);
    setSelectedDomain(nextValue);
    if (validationErrors.domain) {
      clearValidationError('domain');
    }
    setAppState((prevState) => ({
      ...prevState,
      domainParams: {
        ...prevState.domainParams,
        name: nextValue === AUTO_DOMAIN_VALUE
          ? ''
          : domainOptions.find((option) => option?.value === nextValue)?.label || "",
      },
    } as IUpsertAppRequest));
  };

  const handleUseOfferwallPathChange = (_value: any, checked: boolean) => {
    offerwallPathRequestRef.current += 1;
    offerwallPathRequestInFlightRef.current = false;
    setIsGeneratingOfferwallPath(false);
    setUseOfferwallPath(checked);
    clearValidationError('offerwallPath');
    setAppState((current) => ({
      ...current,
      offerwallParams: {
        ...(current.offerwallParams || {}),
        path: checked ? String(current.offerwallParams?.path || '').trim() : '',
      },
    } as IUpsertAppRequest));
  };

  const handleSaveStub = (params: IUpsertStubRequest) => {
     setAppState((prevState: IApp | IUpsertAppRequest) =>
      prevState
        ? ({
            ...prevState,
            // offersStubParams: params.offersStubParams, // Removed - field no longer exists
          } as IApp | IUpsertAppRequest)
        : prevState
    );
   setAppStub(params)
  };

  const copyTrackingParams = () => {
    const trackingParams = (appState as any).campaign?.trackingParams;
    if (trackingParams) {
      const { naming, advertising_id, appsflyer_device_id } = trackingParams;
      const tabSeparatedValues = `${naming || ''}\t${appsflyer_device_id || ''}\t${advertising_id || ''}`;
      navigator.clipboard.writeText(tabSeparatedValues).then(() => {
        toaster.push(message("success", "Параметри трекінгу скопійовано"), { placement, duration: 3000 });
      }).catch(() => {
        toaster.push(message("error", "Помилка копіювання"), { placement, duration: 3000 });
      });
    }
  };

  const renderTabContent = () => {
    const appIdValue = Number((appState as any)?.id || 0);

    switch (activeTab) {
      case "general":
        return (
          <div className="content">
            <div className="general-layout">
              <div className="general-container-left">
                {submitError && (
                  <div className="app-submit-error" role="alert">
                    {submitError}
                  </div>
                )}
                {create && !app && (
                  <div
                    className={`app-id-create-card ${validationErrors.appId ? 'has-error' : ''}`}
                    data-validation-field="appId"
                  >
                    <div className="app-id-create-copy">
                      <span>App ID</span>
                      <strong>{appIdValue > 0 ? `#${appIdValue}` : 'Введіть ID'}</strong>
                    </div>
                    <div className="app-id-create-control">
                      <Input
                        value={appIdValue > 0 ? String(appIdValue) : ''}
                        onChange={handleAppIdChange}
                        placeholder="Наприклад, 42"
                        inputMode="numeric"
                        autoComplete="off"
                        aria-invalid={Boolean(validationErrors.appId)}
                        aria-describedby={validationErrors.appId ? 'app-id-error' : undefined}
                      />
                      {validationErrors.appId && (
                        <span id="app-id-error" className="app-field-error" role="alert">
                          {validationErrors.appId}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* PDL creates offerwall applications only. */}
                    <div
                      className={`offerwall-path-container app-form-field ${validationErrors.offerwallPath ? 'has-error' : ''}`}
                      data-validation-field="offerwallPath"
                    >
                      <Checkbox
                        checked={useOfferwallPath}
                        onChange={handleUseOfferwallPathChange}
                      >
                        Використовувати шлях офервола
                      </Checkbox>
                      <Input
                        value={useOfferwallPath
                          ? (isGeneratingOfferwallPath
                              ? 'Генеруємо унікальний шлях...'
                              : String(appState.offerwallParams?.path || ''))
                          : 'Корінь домену /'}
                        readOnly
                        aria-invalid={Boolean(validationErrors.offerwallPath)}
                      />
                      {validationErrors.offerwallPath && (
                        <span className="app-field-error" role="alert">
                          {validationErrors.offerwallPath}
                        </span>
                      )}
                      {!validationErrors.offerwallPath && (
                        <span className="app-field-help">
                          {useOfferwallPath
                            ? 'PDL генерує унікальний шестисимвольний шлях і передає його на сервер разом із додатком.'
                            : 'Офервол буде розміщено прямо в корені домену. На одному домені може бути лише один кореневий офервол.'}
                        </span>
                      )}
                      {appState.domainParams?.name && (!useOfferwallPath || appState.offerwallParams?.path) && (() => {
                        const suffix = useOfferwallPath ? `${appState.offerwallParams?.path}` : '';
                        const url = `https://${appState.domainParams?.name}/${suffix}`;
                        return (
                          <div className="offerwall-path-preview">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {url}
                            </a>
                            <FontAwesomeIcon
                              icon={faCopy}
                              onClick={() => navigator.clipboard.writeText(url)}
                            />
                          </div>
                        );
                      })()}
                    </div>

                    <div className="sections-list-container" style={{ marginBottom: '16px' }}>
                      <label>Список оферів</label>
                      <SelectPicker
                        data={sectionsLists.map((list) => ({
                          label: list.name,
                          value: list.id,
                        }))}
                        value={appState.offerwallParams?.sectionsListId || null}
                        onChange={handleSectionsListChange}
                        searchable
                    loading={false}
                    locale={{ loading: '', searchPlaceholder: 'Пошук', noResultsText: 'Немає результатів' }}
                        placeholder="Оберіть список оферів"
                        style={{ width: "100%", marginTop: '8px' }}
                        container={() => document.body}
                        placement="autoVerticalStart"
                        menuStyle={{ zIndex: 10000 }}
                      />
                    </div>

                <div className="campaign-mode-container" style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px' }}>
                    Кампанія
                    {(() => {
                      const cId = selectedCampaignId || (appState as any).campaign?.campaignId;
                      if (!cId) return null;
                      return (
                        <a
                          href={`https://et-tracker.com/admin/#!/campaigns/${cId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          style={{ marginLeft: '8px', color: '#3498db', fontSize: '12px' }}
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} /> #{cId}
                        </a>
                      );
                    })()}
                  </label>
                  {!(app as any)?.campaign?.campaignId ? (
                  <RadioGroup
                    name="campaignModeGroup"
                    inline
                    appearance="picker"
                    value={campaignMode}
                    onChange={(value) => setCampaignMode(value as 'create' | 'select')}
                  >
                    <Radio value="create">Створити нову</Radio>
                    <Radio value="select">Обрати існуючу</Radio>
                  </RadioGroup>
                  ) : null}
                  {campaignMode === 'select' && (
                    <SelectPicker
                      data={appxCampaigns.map((c) => {
                        const usedByAppId = usedCampaignMap.get(c.id);
                        const isUsed = usedByAppId !== undefined && usedByAppId !== appState.id;
                        return {
                          label: isUsed ? `#${c.id} — ${c.name} (зайнята: #${usedByAppId})` : `#${c.id} — ${c.name}`,
                          value: c.id,
                        };
                      })}
                      disabledItemValues={
                        Array.from(usedCampaignMap.entries())
                          .filter(([, appId]) => appId !== appState.id)
                          .map(([campaignId]) => campaignId)
                      }
                      value={selectedCampaignId}
                      onChange={(value) => {
                        setSelectedCampaignId(value);
                        if (value && (!appState.name || appState.name.trim() === '')) {
                          const campaign = appxCampaigns.find((c) => c.id === value);
                          if (campaign?.name) {
                            const cleanName = campaign.name.replace(/\[(?:◈|➥)\]\s*/g, '').trim();
                            setAppState((prev) => ({ ...prev, name: cleanName } as IUpsertAppRequest));
                          }
                        }
                      }}
                      searchable
                      placeholder="Пошук кампанії..."
                      locale={{ searchPlaceholder: 'Пошук', noResultsText: 'Немає результатів' }}
                      style={{ width: '100%', marginTop: '8px' }}
                      container={() => document.body}
                      placement="autoVerticalStart"
                      menuStyle={{ zIndex: 10000 }}
                      menuMaxHeight={300}
                    />
                  )}
                </div>

                {/* General Title */}
                <div className="field-group general-title-group" style={{ padding: 0, margin: '16px 0 8px 0' }}>
                  <div className="group-header" style={{ padding: 0, display: 'flex', alignItems: 'center' }}>
                    <div style={{ width: '3px', height: '20px', backgroundColor: '#27ae60', marginRight: '8px' }}></div>
                    <h3 style={{ textTransform: 'none', color: '#ffffff', margin: 0, fontSize: '16px' }}>Головне</h3>
                  </div>
                </div>

                <div
                  className={`app-platform-container app-form-field ${validationErrors.appPlatform ? 'has-error' : ''}`}
                  data-validation-field="appPlatform"
                  style={{ marginBottom: '16px' }}
                >
                  <label>Платформа <span style={{ color: '#e74c3c' }}>*</span></label>
                  <SelectPicker
                    data={[
                      { label: 'Android', value: AppPlatform.ANDROID },
                      { label: 'iOS', value: AppPlatform.IOS },
                    ]}
                    value={(appState as any).appPlatform || null}
                    onChange={(value) => {
                      setAppState((prev) => ({ ...prev, appPlatform: value } as any));
                      if (validationErrors.appPlatform) {
                        clearValidationError('appPlatform');
                      }
                    }}
                    searchable={false}
                    cleanable={false}
                    placeholder="Оберіть платформу"
                    style={{ width: '100%', marginTop: '8px' }}
                    container={() => document.body}
                    placement="autoVerticalStart"
                    menuStyle={{ zIndex: 10000 }}
                  />
                  {validationErrors.appPlatform && (
                    <span className="app-field-error" role="alert">{validationErrors.appPlatform}</span>
                  )}
                </div>

                <div className="geos-container" style={{ marginBottom: '16px' }}>
                  <label>Гео</label>
                  <TagPicker
                    data={[
                      'US','GB','CA','AU','DE','FR','IT','ES','PT','NL','BE','AT','CH','SE','NO','DK','FI','IE','PL','CZ',
                      'RO','HU','BG','HR','SK','SI','LT','LV','EE','GR','UA','RU','BY','KZ','UZ','GE','AM','AZ','MD',
                      'TR','IL','SA','AE','QA','KW','BH','OM','EG','MA','TN','NG','KE','ZA','GH','TZ',
                      'IN','PK','BD','LK','NP','PH','ID','MY','SG','TH','VN','KH','MM','JP','KR','TW','HK','CN',
                      'BR','MX','AR','CO','CL','PE','EC','VE','UY','PY','BO','CR','PA','DO','GT','HN','SV','NI',
                      'NZ','FJ'
                    ].map(code => {
                      const flag = code.toUpperCase().split('').map((c) => String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))).join('');
                      return { label: `${flag} ${code}`, value: code };
                    })}
                    value={[...(appState.geos || [])].sort()}
                    onChange={(value) => {
                      setAppState((prev) => ({ ...prev, geos: value || [] } as any));
                    }}
                    searchable
                    placeholder="Оберіть країни"
                    style={{ width: '100%', marginTop: '8px' }}
                    container={() => document.body}
                    placement="autoVerticalStart"
                    menuStyle={{ zIndex: 10000 }}
                  />
                </div>

                <div
                  className={`domain-name-container app-form-field ${validationErrors.domain ? 'has-error' : ''}`}
                  data-validation-field="domain"
                >
                  <label>Домен {!create && <span style={{ color: '#e74c3c' }}>*</span>}</label>
                  <SelectPicker
                    data={domainOptions}
                    value={selectedDomain}
                    onChange={handleDomainChange}
                    onOpen={() => {
                      setTimeout(() => {
                        const searchInput = document.querySelector('.rs-search-box-input') as HTMLInputElement;
                        if (searchInput) {
                          searchInput.focus();
                        }
                      }, 100);
                    }}
                    searchable
                    placeholder="Оберіть домен"
                    locale={{ searchPlaceholder: 'Пошук' }}
                    menuMaxHeight={400}
                    cleanable={Boolean(create && !app)}
                    style={{ width: "100%" }}
                    container={() => document.body}
                    placement="autoVerticalStart"
                    menuStyle={{ zIndex: 10000 }}
                  />
                  {create && !app && !validationErrors.domain && (
                    <span className="app-field-help">
                      Якщо не обирати наявний домен, PDL автоматично придбає найдешевший доступний Namecheap-домен і прив’яже його до додатку.
                    </span>
                  )}
                  {validationErrors.domain && (
                    <span className="app-field-error" role="alert">{validationErrors.domain}</span>
                  )}
                </div>

                <div
                  className={`bundle-container app-form-field ${validationErrors.bundle ? 'has-error' : ''}`}
                  data-validation-field="bundle"
                >
                  <label>Bundle <span style={{ color: '#ff5722' }}>*</span></label>
                  <Input
                    value={appState.bundle || ""}
                    onChange={(value) => {
                      handleBundleChange(value);
                      if (validationErrors.bundle) {
                        clearValidationError('bundle');
                      }
                    }}
                    style={{ width: "100%" }}
                    aria-invalid={Boolean(validationErrors.bundle)}
                  />
                  {validationErrors.bundle && (
                    <span className="app-field-error" role="alert">
                      {validationErrors.bundle}
                    </span>
                  )}
                </div>

                <div
                  className={`bundle-container app-form-field ${validationErrors.name ? 'has-error' : ''}`}
                  data-validation-field="name"
                >
                  <label>Назва <span style={{ color: '#ff5722' }}>*</span></label>
                  <Input
                    value={appState.name || ""}
                    onChange={(value) => {
                      handleNameChange(value);
                      if (validationErrors.name) {
                        clearValidationError('name');
                      }
                    }}
                    style={{ width: "100%" }}
                    aria-invalid={Boolean(validationErrors.name)}
                  />
                  {validationErrors.name && (
                    <span className="app-field-error" role="alert">
                      {validationErrors.name}
                    </span>
                  )}
                </div>

                {(() => {
                  const trackingParams = (appState as any).campaign?.trackingParams;
                  return trackingParams && (
                  <div className="tracking-params-container" style={{ display: 'none' }}>
                    <label style={{ fontSize: '12px', color: '#8e8e93', marginBottom: '8px', display: 'block' }}>Параметри трекінгу</label>
                    <div className="tracking-params-block" style={{ 
                      display: 'flex', 
                      gap: '12px',
                      alignItems: 'center',
                      backgroundColor: 'rgb(36 40 48)',
                      padding: '12px',
                      borderRadius: '6px',
                      border: '1px solid rgb(61 63 67)',
                      fontFamily: 'monospace',
                      fontSize: '13px',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                    }}>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        flex: 1
                      }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Naming:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid rgb(61 63 67'
                            }}>
                              {trackingParams.naming || 'не вказано'}
                            </div>
                          </div>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Device ID:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid #e5e7eb'
                            }}>
                              {trackingParams.appsflyer_device_id || 'не вказано'}
                            </div>
                          </div>
                          <div style={{ minWidth: '80px' }}>
                            <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '2px' }}>Advertising ID:</div>
                            <div style={{ 
                              padding: '4px 8px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '4px',
                              color: '#374151',
                              border: '1px solid #e5e7eb'
                            }}>
                              {trackingParams.advertising_id || 'не вказано'}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={copyTrackingParams}
                        style={{
                          background: '#3b82f6',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          color: '#ffffff',
                          fontSize: '12px',
                          fontWeight: '500',
                          gap: '6px',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
                        title="Копіювати всі параметри (розділені табами для вставки в Google Sheets)"
                      >
                        <CopyIcon />
                        Копіювати
                      </button>
                    </div>
                  </div>
                )})()}
              </div>

            </div>
          </div>
        );
      // Settings tab removed - merged into general tab
      case "json":
        return appState ? (
          <div onClick={handleEditAttempt}>
            <AppJSON
              app={appState}
              isEditable={isEditingAllowed}
              updateApp={updateApp}
            />
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className={`upsert-app-backdrop ${isClosing ? 'closing' : ''}`} onClick={(e) => {
      // Close modal if clicking on backdrop (not on modal content)
      if (e.target === e.currentTarget && !hasChanges) {
        closeWithAnimation();
      }
    }}>
      <div className="upsert-app-container" onClick={(e) => e.stopPropagation()}>
        <div className="upsert-app-header">
        <div className="upsert-app-header-content">
          <div className="upsert-app-id-section">
            <span 
              className="upsert-app-id" 
              style={{ 
                color: Number((appState as any)?.id || 0) > 0 ? '#169de0' : '#9aa4b2',
                fontSize: Number((appState as any)?.id || 0) > 0 ? '24px' : '18px',
                fontWeight: 'bold'
              }}
            >
              {Number((appState as any)?.id || 0) > 0 ? `#${Number((appState as any).id)}` : 'Нова апка'}
            </span>
          </div>
          <div className="upsert-app-actions">
            <RadioGroup 
              name="tabGroup" 
              className="tabGroup" 
              onChange={value => setActiveTab(value as TabKey)} 
              inline 
              appearance="picker" 
              value={activeTab}
            >
              <Radio value="general">Загальне</Radio>
              <Radio value="json">JSON</Radio>
            </RadioGroup>
          </div>
          <div className="upsert-app-search">
            <div className="upsert-app-buttons">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                className="lists-tab save-btn"
                onClick={handleSave}
                disabled={!hasChanges || isSaving || isGeneratingOfferwallPath}
              >
                <SaveIcon /> {isSaving ? 'Збереження...' : 'Зберегти'}
              </button>
              </div>
              <span className="upsert-app-separator" />
              <button
                className="lists-tab close-btn"
                onClick={closeWithAnimation}
              >
                <CloseIcon /> Закрити
              </button>
            </div>
          </div>
        </div>
      </div>

        <div className="upsert-app-content">
          {renderTabContent()}
        </div>

      </div>
    </div>
  );
}
